// src/admin/purge/TypedConfirmDialog.jsx — the confirmation of an IRREVERSIBLE superadmin action (v206, D1).
//
// The summary (children), an optional blocker sentence (with «Riprova» when `onRetry` is given — a blocker such as
// PURGE_IN_PROGRESS clears by itself, and the dialog stays locked until the preview is read again), a field where the operator types the target again, and the
// danger button — enabled only on an exact match (`caseInsensitive` for an email; never trimmed: the backend does
// not trim, and the text is sent as typed). ⛔ Enter never confirms (CLAUDE.md: long/irreversible actions start
// from their button only) — the field is not in a <form> and swallows Enter. ⛔ An overlay click does not close it
// (a half-typed confirmation must not vanish); × / Escape / Annulla do, except while the action runs. A failure is
// worded from err.code (adminErrorCopy, purge wording) and the dialog stays open; success is the caller's to close.
// v212: `header` (always shown, above the summary — the company mode choice), `resetKey` (clears the field and the
// error when it changes), `sendValue` (sent instead of the typing — the canonical id), `busyText` (shown while the
// action runs), `wordError` (overrides the purge wording); no `expected` = locked.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdminModal from '../components/AdminModal.jsx';
import { adminErrorText } from '../adminErrorCopy.js';
import { COLORS, FONT_MONO, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

export function matchesExpected(typed, expected, caseInsensitive) {
  if (typeof expected !== 'string' || expected === '') return false;
  return caseInsensitive ? typed.toLowerCase() === expected.toLowerCase() : typed === expected;
}

const retryButton = {
  display: 'block', marginTop: '8px', padding: '4px 12px', borderRadius: RADII.controlInner,
  border: `1px solid ${SEMANTIC.errore.fg}`, background: COLORS.surface, color: SEMANTIC.errore.fg,
  fontSize: '12px', fontWeight: 600, fontFamily: FONT_SANS, cursor: 'pointer',
};

const alertBox = {
  marginBottom: '16px', padding: '10px 14px', borderRadius: RADII.controlInner, background: SEMANTIC.errore.bg,
  border: `1px solid ${SEMANTIC.errore.fg}`, color: SEMANTIC.errore.fg, fontSize: '13px', fontFamily: FONT_SANS,
};

export default function TypedConfirmDialog({
  open, title, children, expected, caseInsensitive = false, inputLabel, confirmLabel,
  blockedText = null, loading = false, onRetry = null, onConfirm, onClose,
  header = null, resetKey = null, sendValue, busyText = null, wordError = null,
}) {
  const { t } = useTranslation('admin');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setTyped(''); setBusy(false); setError(''); }
  }, [open]);

  // A new target (the company dialog's mode) must be confirmed again from scratch.
  useEffect(() => {
    setTyped('');
    setError('');
  }, [resetKey]);

  const locked = busy || loading || Boolean(blockedText) || !expected;
  const canConfirm = !locked && matchesExpected(typed, expected, caseInsensitive);

  async function handleConfirm() {
    if (!canConfirm) return;
    setBusy(true);
    setError('');
    try {
      await onConfirm(sendValue !== undefined ? sendValue : typed);
    } catch (err) {
      setError(wordError ? wordError(err) : adminErrorText(t, err, { purge: true }));
    } finally {
      setBusy(false);
    }
  }

  function handleClose() {
    if (!busy) onClose();
  }

  return (
    <AdminModal open={open} onClose={handleClose} title={title} width={560} role="dialog" closeOnOverlay={false}>
      {header}
      {loading ? (
        <p style={{ color: COLORS.textSecondary, fontSize: '13px', fontFamily: FONT_SANS }}>{t('purge.loading')}</p>
      ) : children}
      {blockedText && (
        <div role="alert" style={alertBox}>
          {blockedText}
          {onRetry && (
            <button type="button" onClick={onRetry} style={retryButton}>{t('purge.retry')}</button>
          )}
        </div>
      )}
      <label style={{ display: 'block', margin: '12px 0 16px', fontSize: '13px', color: COLORS.ink, fontFamily: FONT_SANS }}>
        {inputLabel}
        <input
          type="text"
          aria-label={inputLabel}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
          disabled={locked}
          autoComplete="off"
          spellCheck={false}
          style={{
            display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '6px', padding: '8px 12px',
            borderRadius: RADII.controlInner, border: `1px solid ${COLORS.borderStrong}`, fontFamily: FONT_MONO,
            fontSize: '13px',
          }}
        />
      </label>
      {busy && busyText && (
        <p role="status" style={{ color: COLORS.textSecondary, fontSize: '12px', margin: '0 0 12px', fontFamily: FONT_SANS }}>
          {busyText}
        </p>
      )}
      {error && <div role="alert" style={alertBox}>{error}</div>}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={handleClose}
          disabled={busy}
          style={{
            padding: '8px 16px', borderRadius: RADII.controlInner, border: `1px solid ${COLORS.borderStrong}`,
            background: COLORS.surface, color: COLORS.ink, fontSize: '13px', fontFamily: FONT_SANS,
            cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1,
          }}
        >
          {t('common:actions.cancel')}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canConfirm}
          style={{
            padding: '8px 16px', borderRadius: RADII.controlInner, border: 'none',
            background: canConfirm ? SEMANTIC.errore.fg : COLORS.borderStrong, color: COLORS.surface,
            fontSize: '13px', fontWeight: 600, fontFamily: FONT_SANS, cursor: canConfirm ? 'pointer' : 'not-allowed',
          }}
        >
          {busy ? t('confirm.working') : confirmLabel}
        </button>
      </div>
    </AdminModal>
  );
}
